import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getBaseApiUrl } from '../../config/api';
import { consentAllows } from '../../features/consent/consentStore';
import { checkoutRefusalMessage, clearPendingCheckout } from '../../features/checkout/checkoutRefusal';

export const NOTHING_GRANTED_MESSAGE =
  "We couldn't add this purchase to your account, so nothing has been unlocked. "
  + 'If you were charged, contact us and we will put it right.';

const PACKAGE_KEY_PREFIX = 'interview_';

// The exam purchase among what the server granted (package keys are not
// exams). Falls back to the plan named in the return URL.
export function grantedExamKey(granted, fallback) {
  const items = Array.isArray(granted) ? granted : [];
  const match = items.find((g) => g && typeof g.plan_key === 'string' && !g.plan_key.startsWith(PACKAGE_KEY_PREFIX));
  return match ? match.plan_key : fallback;
}

// Ads conversion value from the exam tier: Foundations 60, Beyond 80.
export function conversionValue(examKey) {
  return (examKey || '').includes('beyond') ? 80.0 : 60.0;
}

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [refusalMessage, setRefusalMessage] = useState(null);

  const planKey = searchParams.get('plan') || 'lnat_foundations';
  const sessionId = searchParams.get('session_id') || searchParams.get('simulated');

  useEffect(() => {
    let isMounted = true;

    const activateSubscriptionAndRedirect = async () => {
      let refused = false;
      let grantedKey = planKey;
      try {
        const apiUrl = getBaseApiUrl();
        const userRaw = localStorage.getItem('pathbuddy_user') || sessionStorage.getItem('pathbuddy_user') || localStorage.getItem('user');
        const user = userRaw ? (typeof userRaw === 'string' && userRaw.startsWith('{') ? JSON.parse(userRaw) : { email: userRaw }) : null;
        const accessToken =
          localStorage.getItem('pathbuddy_access_token')
          || sessionStorage.getItem('pathbuddy_access_token')
          || localStorage.getItem('token')
          || '';

        const headers = { 'Content-Type': 'application/json' };
        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }

        const res = await fetch(`${apiUrl}/api/stripe/user-subscription`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            // Identity comes from JWT + verified Stripe session — body ids are not trusted.
            plan: planKey,
            session_id: sessionId
          })
        });

        const data = await res.json().catch(() => ({}));

        // Organisation member: the server refused to activate this purchase
        // (the payment is refunded by hand). Show why, write no plan, and stay here.
        const refusal = checkoutRefusalMessage(res, data);
        if (refusal) {
          refused = true;
          clearPendingCheckout();
          if (isMounted) setRefusalMessage(refusal);
          return;
        }

        // The server reports what the session granted. An empty list means
        // nothing was written: say so, report no conversion, stay here.
        // (A response with no list at all is an older server and carries on.)
        if (Array.isArray(data.granted) && data.granted.length === 0) {
          refused = true;
          clearPendingCheckout();
          if (isMounted) setRefusalMessage(NOTHING_GRANTED_MESSAGE);
          return;
        }
        grantedKey = grantedExamKey(data.granted, planKey);

        // Trigger Google Ads Purchase Conversion Event (Advertising consent only)
        if (window.gtag && consentAllows('advertising')) {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-18348840584/DwpVCLGv_dscEIittK1E',
            'value': conversionValue(grantedKey),
            'currency': 'GBP',
            'transaction_id': sessionId || ''
          });
        }

        // Only persist tokens when the API issued them for the authenticated buyer.
        if (data.token && data.user && !data.login_required) {
          const userStr = JSON.stringify(data.user);
          localStorage.setItem('user', userStr);
          localStorage.setItem('pathbuddy_user', userStr);
          sessionStorage.setItem('pathbuddy_user', userStr);
          localStorage.setItem('token', data.token);
          localStorage.setItem('pathbuddy_access_token', data.token);
          sessionStorage.setItem('pathbuddy_access_token', data.token);
          window.dispatchEvent(new Event('authChange'));
        } else if (user) {
          // No plan is written client-side (the server never read it, and the
          // response may describe a different account than the stored one).
          // AuthContext re-reads the signed-in user from GET /api/auth/me on
          // authChange, so the page shows the plan the server granted.
          window.dispatchEvent(new Event('authChange'));
        }
      } catch (err) {
        console.error("Subscription sync error:", err);
      } finally {
        if (isMounted && !refused) {
          const examSource = grantedKey || planKey;
          const exam = searchParams.get('exam') || (examSource.includes('lnat') ? 'lnat' : examSource.includes('tmua') ? 'tmua' : examSource.includes('ucat') ? 'ucat' : examSource.includes('esat') ? 'esat' : null);
          if (exam) {
            navigate(`/practice/${exam}`, { replace: true });
          } else {
            navigate('/apps', { replace: true });
          }
        }
      }
    };

    activateSubscriptionAndRedirect();

    return () => {
      isMounted = false;
    };
  }, [planKey, sessionId, navigate, searchParams]);

  if (refusalMessage) {
    return (
      <div className="subscription-success-refusal" role="alert" style={{ maxWidth: 560, margin: '80px auto', padding: '0 16px', textAlign: 'center' }}>
        <p>{refusalMessage}</p>
        <button type="button" onClick={() => navigate('/apps', { replace: true })}>
          Continue
        </button>
      </div>
    );
  }

  return null;
}
